import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Modal, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag, type StatusTone } from "@/shared/components/StatusTag";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useDeleteGpsDevice, useGpsDevices } from "../hooks/useGpsDevices";
import { useGpsDevicesStore } from "../store/gps-devices.store";
import type { GpsDevice } from "../types";

interface GpsDeviceTableProps {
	organizationId: string;
	page?: number;
	limit?: number;
	onPageChange?: (page: number) => void;
	onLimitChange?: (limit: number) => void;
}

const STATUS_COLORS: Record<string, StatusTone> = {
	online: "success",
	offline: "default",
	error: "error",
};

const STATUS_LABELS: Record<string, string> = {
	online: "Online",
	offline: "Offline",
	error: "Xato",
};

export function GpsDeviceTable({
	organizationId,
	page = 1,
	limit = 10,
	onPageChange,
	onLimitChange,
}: GpsDeviceTableProps) {
	const { data, isLoading } = useGpsDevices({ organizationId, page, limit });
	const { mutate: deleteGpsDevice } = useDeleteGpsDevice();
	const openEditModal = useGpsDevicesStore((s) => s.openEditModal);

	const handleDelete = (record: GpsDevice) => {
		Modal.confirm({
			title: "O'chirish",
			content: "Rostdan ham o'chirmoqchimisiz?",
			okText: "Ha",
			cancelText: "Yo'q",
			okButtonProps: { danger: true },
			onOk: () => deleteGpsDevice(record.id),
		});
	};

	const columns: ColumnsType<GpsDevice> = [
		{
			title: "IMEI",
			dataIndex: "imei",
			key: "imei",
			width: 180,
			render: (imei: string) => <span className="font-mono">{imei}</span>,
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status: string) => (
				<StatusTag tone={STATUS_COLORS[status] ?? "default"}>
					{STATUS_LABELS[status] ?? status}
				</StatusTag>
			),
		},
		{
			title: "Oxirgi ulanish",
			dataIndex: "lastConnectionAt",
			key: "lastConnectionAt",
			width: 180,
			render: (date: string | null) =>
				date ? (
					<span className="font-mono">
						{new Date(date).toLocaleDateString("uz-UZ", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit",
							hour: "2-digit",
							minute: "2-digit",
						})}
					</span>
				) : (
					"—"
				),
		},
		{
			title: "Yaratilgan",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 160,
			render: (date: string) => (
				<span className="font-mono">
					{new Date(date).toLocaleDateString("uz-UZ", {
						year: "numeric",
						month: "2-digit",
						day: "2-digit",
						hour: "2-digit",
						minute: "2-digit",
					})}
				</span>
			),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: GpsDevice) => {
				const menuItems: MenuProps["items"] = [
					{
						key: "edit",
						icon: <EditOutlined />,
						label: "Tahrirlash",
						onClick: () => openEditModal(record),
					},
					{
						key: "delete",
						icon: <DeleteOutlined />,
						label: "O'chirish",
						danger: true,
						onClick: () => handleDelete(record),
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<Button type="text" icon={<MoreOutlined />} size="small" />
					</Dropdown>
				);
			},
		},
	];

	const items = data?.data?.items ?? [];
	const total = data?.data?.meta?.total ?? 0;

	return (
		<Table
			columns={columns}
			dataSource={items}
			loading={isLoading}
			rowKey="id"
			pagination={tablePagination({
				page,
				limit,
				total,
				onChange: (newPage, newLimit) => {
					onPageChange?.(newPage);
					onLimitChange?.(newLimit);
				},
			})}
			size="middle"
			locale={{
				emptyText: <EmptyState description="GPS qurilmalar yo'q" />,
			}}
		/>
	);
}
